import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Icon, Logo } from "../components/Icon";
import { QR } from "../components/QR";
import { isNative } from "../lib/platform";
import { load, save } from "../lib/storage";
import { absoluteUrl, latestRelease, type Release } from "../lib/updates";

/** The front page for people who aren't signed in: the full landing on the web, one screen in the app. */
export function Landing() {
  return isNative ? <Welcome /> : <WebLanding />;
}

function Wordmark({ size = 24 }: { size?: number }) {
  return (
    <span className="lp-brand">
      <Logo size={size + 4} />
      <span className="lp-d" style={{ fontSize: size }}>omni</span>
    </span>
  );
}

/** A small picture of a space: a Finance widget, app icons and a to-do card. */
function SpacePreview({ tall }: { tall?: boolean }) {
  return (
    <div className="lp-grid" aria-hidden="true">
      <div className="lp-w lp-w-big">
        <span className="lp-w-title"><span className="lp-w-dot" />Finance</span>
        <span className="lp-w-label">Spent this month</span>
        <span className="lp-w-num">4 812,40 zł</span>
        <span className="lp-bar"><i style={{ flexGrow: 1380, background: "#65A30D" }} /><i style={{ flexGrow: 1240, background: "#7C6FE0" }} /><i style={{ flexGrow: 1150, background: "#0F9A87" }} /><i style={{ flexGrow: 620, background: "#D98A1F" }} /><i style={{ flexGrow: 422, background: "#B5BCB7" }} /></span>
      </div>
      <span className="lp-app"><span className="lp-app-ic" data-tone="lime"><Icon name="chart" size={22} /></span>Finance</span>
      <span className="lp-app"><span className="lp-app-ic" data-tone="blue"><Icon name="check" size={22} /></span>Tasks</span>
      <span className="lp-app"><span className="lp-app-ic" data-tone="amber"><Icon name="globe" size={22} /></span>Location</span>
      <span className="lp-app"><span className="lp-app-ic lp-app-add"><Icon name="plus" size={22} /></span>Add</span>
      <div className="lp-w lp-w-row">
        <span className="lp-w-sq" />
        <span className="lp-w-row-t">3 receipts to review</span>
        <span className="lp-w-btn">Review</span>
      </div>
      {tall && (
        <>
          <div className="lp-w lp-w-half"><span className="lp-w-label">Balance</span><span className="lp-w-num sm">24 380 zł</span></div>
          <div className="lp-w lp-w-half"><span className="lp-w-label">Open tasks</span><span className="lp-w-num sm">7 today</span></div>
        </>
      )}
    </div>
  );
}

function AndroidButton({ release, wide }: { release: Release; wide?: boolean }) {
  return (
    <a className={`lp-dl${wide ? " wide" : ""}`} href={absoluteUrl("/api/app/download/android")} download={release.filename}>
      <Icon name="phone" size={24} />
      <span><small>Download for</small><b>Android</b></span>
    </a>
  );
}

const NAV = [
  { href: "#how", label: "How it works" },
  { href: "#finance", label: "Finance" },
  { href: "#privacy", label: "Privacy" },
  { href: "#developers", label: "For developers" },
];

function WebLanding() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [release, setRelease] = useState<Release | null>(null);
  const [menu, setMenu] = useState(false);
  const [banner, setBanner] = useState(() => !load<boolean>("omni.landing.bannerClosed", false));
  const android = /android/i.test(navigator.userAgent);

  useEffect(() => {
    latestRelease().then(setRelease).catch(() => setRelease(null));
  }, []);

  const go = (to: "register" | "login") => (e?: FormEvent) => {
    e?.preventDefault();
    nav(`/${to}${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`);
  };
  const closeBanner = () => {
    setBanner(false);
    save("omni.landing.bannerClosed", true);
  };
  const scrollTo = (href: string) => {
    setMenu(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lp">
      {release && android && banner && (
        <div className="lp-banner">
          <span className="lp-banner-ic"><Logo size={22} /></span>
          <span className="lp-banner-t"><b>Omni works best in the app</b><small>Free download for Android</small></span>
          <a className="lp-pill" href={absoluteUrl("/api/app/download/android")} download={release.filename}>Get</a>
          <button className="lp-x" aria-label="Close" onClick={closeBanner}><Icon name="close" size={18} /></button>
        </div>
      )}

      <header className="lp-nav">
        <Link to="/" className="lp-home" aria-label="Omni home"><Wordmark /></Link>
        <nav aria-label="Primary" className="lp-links">
          {NAV.map((n) => <a key={n.href} href={n.href} onClick={(e) => { e.preventDefault(); scrollTo(n.href); }}>{n.label}</a>)}
        </nav>
        <Link className="lp-signin" to="/login">Sign in</Link>
        {release && <a className="lp-btn ink lp-getapp" href="#download" onClick={(e) => { e.preventDefault(); scrollTo("#download"); }}><Icon name="download" size={20} />Get the app</a>}
        <button className="lp-menu-btn" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}><Icon name={menu ? "close" : "menu"} /></button>
      </header>
      {menu && (
        <nav className="lp-menu" aria-label="Sections">
          {NAV.map((n) => <a key={n.href} href={n.href} onClick={(e) => { e.preventDefault(); scrollTo(n.href); }}>{n.label}</a>)}
          {release && <a href="#download" onClick={(e) => { e.preventDefault(); scrollTo("#download"); }}>Get the app</a>}
        </nav>
      )}

      <section className="lp-hero">
        <div className="lp-hero-text">
          <span className="lp-eyebrow">Your personal ERP</span>
          <h1 className="lp-d lp-h1">One sign-in for everything you run.</h1>
          <p className="lp-lede">Money, tasks, places. Each gets its own app, and they all live in one space you arrange yourself. Apps only share data when you say so.</p>
          <form className="lp-card lp-signup" onSubmit={go("register")}>
            <label htmlFor="lp-email" className="lp-label">Create your space or sign in</label>
            <div className="lp-field">
              <input id="lp-email" type="email" placeholder="you@example.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button className="lp-btn pri">Create your space</button>
            </div>
            <span className="lp-small">Already have an account? <Link to={`/login${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`}>Sign in</Link> · Got an invite? <Link to="/login">Enter your code</Link></span>
          </form>
          <div className="lp-mobile-cta">
            <button className="lp-btn pri" onClick={() => go("register")()}>Create your space</button>
            <button className="lp-btn sec" onClick={() => go("login")()}>Sign in</button>
          </div>
          {release && (
            <div className="lp-dl-row">
              <AndroidButton release={release} />
              <span className="lp-small lp-or">or keep using it right here in the browser</span>
            </div>
          )}
        </div>
        <div className="lp-hero-art">
          <div className="lp-phone">
            <div className="lp-phone-screen">
              <div className="lp-phone-bar"><Logo size={18} /><span className="lp-d">Space</span><span className="lp-av">AL</span></div>
              <div className="lp-phone-body"><SpacePreview tall /></div>
              <div className="lp-phone-tabs"><i className="on" /><i /><i /><i /></div>
            </div>
          </div>
        </div>
        <div className="lp-hero-panel"><SpacePreview /></div>
      </section>

      <section id="how" className="lp-how">
        <div className="lp-head">
          <span className="lp-eyebrow">How it works</span>
          <h2 className="lp-d lp-h2">A home screen for your life admin.</h2>
        </div>
        <div className="lp-steps">
          <div className="lp-step"><span className="lp-num">1</span><div><h3>Sign in once</h3><p>One account for every app in your space, so you never make another login.</p></div></div>
          <div className="lp-step"><span className="lp-num">2</span><div><h3>Add the apps you need</h3><p>Pick them from the marketplace. Start with Finance, Tasks and Location, and add more as they arrive.</p></div></div>
          <div className="lp-step"><span className="lp-num">3</span><div><h3>Arrange your space</h3><p>Place app icons and live widgets on one grid. See what needs attention without opening anything.</p></div></div>
        </div>
      </section>

      <section id="finance" className="lp-finance">
        <div className="lp-head">
          <span className="lp-eyebrow lime">The first app</span>
          <h2 className="lp-d lp-h2">Finance tells you where your money went, and why.</h2>
          <p className="lp-p lp-wide-only">Built by the Omni team, running in your space like any other app.</p>
        </div>
        <div className="lp-card lp-feats">
          <div className="lp-feat"><span className="lp-fic"><Icon name="bank" size={22} /></span><div><h3>Connect your banks</h3><p>Read-only open banking. Finance sees balances and transactions and can never move money.</p></div></div>
          <div className="lp-feat"><span className="lp-fic"><Icon name="camera" size={22} /></span><div><h3>Scan receipts and invoices</h3><p>Take a photo or upload a PDF. The merchant, date, total and items are read for you and matched to the right payment.</p></div></div>
          <div className="lp-feat"><span className="lp-fic"><Icon name="chat" size={22} /></span><div><h3>Ask why</h3><p>"Why did September cost more?" gets an answer that points to the exact transactions and receipts behind it.</p></div></div>
        </div>
      </section>

      <section id="privacy" className="lp-privacy">
        <div className="lp-head">
          <span className="lp-eyebrow">Privacy</span>
          <h2 className="lp-d lp-h2">Every app is boxed in. You hold the keys.</h2>
        </div>
        <ul>
          <li><Icon name="check" size={20} />Apps run isolated from each other, each with its own store for your data.</li>
          <li><Icon name="check" size={20} />Each app lists the data it wants before you install it.</li>
          <li><Icon name="check" size={20} />Sharing between apps is off until you turn it on, and you can download or delete what each app keeps.</li>
        </ul>
      </section>

      <section id="developers" className="lp-dev">
        <div className="lp-head">
          <span className="lp-eyebrow">For developers</span>
          <h2 className="lp-d lp-h2">Ship your app into Omni without an app-store release.</h2>
          <p className="lp-p">Build with Vite and the Omni SDK, upload your bundle and a manifest, and it appears in the marketplace. Sign-in, billing, events and your own data store come built in.</p>
        </div>
        <pre className="lp-code">{`import { omni } from "@omni/sdk"

const { user } = await omni.ready()
const lists = await omni.api.get("/lists")
omni.events.on("finance.transaction.*", refresh)
omni.ui.toast(\`Hi \${user.display_name}\`)`}</pre>
      </section>

      {release && (
        <section id="download" className="lp-card lp-download">
          <div className="lp-head">
            <h2 className="lp-d lp-h2">Take your space with you.</h2>
            <p className="lp-p">Same account, same apps, same layout on your phone and in the browser. Omni {release.version} for Android.</p>
            <AndroidButton release={release} wide />
          </div>
          <div className="lp-qr">
            <QR value={absoluteUrl("/api/app/download/android")} size={168} />
            <span className="lp-small">Scan with your phone</span>
          </div>
        </section>
      )}

      <footer className="lp-foot">
        <div><Wordmark size={20} /><span className="lp-small">© {new Date().getFullYear()} Omni</span></div>
        <nav aria-label="Footer">
          <Link to="/login">Sign in</Link>
          <Link to="/register">Create an account</Link>
          {release && <a href={absoluteUrl("/api/app/download/android")} download={release.filename}>Android app</a>}
          <Link to="/settings/server">Server</Link>
        </nav>
      </footer>
    </div>
  );
}

/** The phone app: one screen, no scrolling. */
function Welcome() {
  const nav = useNavigate();
  return (
    <div className="lp-welcome">
      <div className="lp-welcome-top">
        <Wordmark size={30} />
      </div>
      <div className="lp-welcome-mid">
        <div className="lp-welcome-art"><SpacePreview /></div>
        <h1 className="lp-d">One sign-in for everything you run.</h1>
        <p>Money, tasks, places. Each in its own app, all in one space you arrange.</p>
      </div>
      <div className="lp-welcome-actions">
        <button className="lp-btn pri" onClick={() => nav("/register")}>Create your space</button>
        <button className="lp-btn sec" onClick={() => nav("/login")}>Sign in</button>
        <Link to="/settings/server" className="lp-small">Server settings</Link>
      </div>
    </div>
  );
}
